import { useState, useEffect } from 'react';
import { FileDown, Loader2 } from 'lucide-react';
import { useAuthStore } from '../store/useStore';
import toast from 'react-hot-toast';

interface Digest {
  id: string;
  title: string;
  createdAt: string;
}

type ExportFormat = 'pdf' | 'epub';

export default function Export() {
  const token = useAuthStore((state) => state.token)!;

  const [format, setFormat] = useState<ExportFormat>('pdf');
  const [includeLiked, setIncludeLiked] = useState(true);
  const [includeDigest, setIncludeDigest] = useState(false);
  const [includeShared, setIncludeShared] = useState(false);
  const [selectedDigestId, setSelectedDigestId] = useState<string>('');
  const [days, setDays] = useState(7);

  const [digests, setDigests] = useState<Digest[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDigests, setLoadingDigests] = useState(true);
  const [progress, setProgress] = useState<string>('');

  useEffect(() => {
    loadDigests();
  }, []);

  async function loadDigests() {
    try {
      const response = await fetch('/api/digests', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to load digests');

      const data = await response.json();
      setDigests(data.digests || []);
    } catch (error) {
      console.error('Failed to load digests:', error);
    } finally {
      setLoadingDigests(false);
    }
  }

  async function handleExport() {
    // Validation
    if (!includeLiked && !includeDigest && !includeShared) {
      toast.error('Please select at least one content source');
      return;
    }

    if (includeDigest && !selectedDigestId) {
      toast.error('Please select a digest');
      return;
    }

    setLoading(true);
    setProgress('Gathering articles...');

    try {
      const requestBody: any = {
        days,
      };

      if (includeLiked) {
        requestBody.includeLiked = true;
      }

      if (includeDigest && selectedDigestId) {
        requestBody.includeDigest = true;
        requestBody.digestId = selectedDigestId;
      }

      if (includeShared) {
        requestBody.includeShared = true;
      }

      setProgress('Categorizing articles...');

      const endpoint = format === 'pdf' ? '/api/export/pdf' : '/api/export/epub';
      const fileExtension = format === 'pdf' ? 'pdf' : 'epub';
      const formatName = format.toUpperCase();

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to generate ${formatName}`);
      }

      setProgress(`Generating ${formatName}...`);

      // Get the file blob
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `My_Reading_Digest_${new Date().toISOString().split('T')[0]}.${fileExtension}`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setProgress('');
      toast.success(`${formatName} downloaded successfully!`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate export');
      setProgress('');
    } finally {
      setLoading(false);
    }
  }

  const formatName = format === 'pdf' ? 'PDF' : 'EPUB';

  return (
    <div className="min-h-full bg-canvas page-transition">
      <div className="pl-4 pr-4 lg:pl-6 lg:pr-8 py-8">
        <div className="max-w-prose">
          <div className="kicker mb-1">Export</div>
          <h1 className="text-3xl mb-2">Take your reading with you.</h1>
          <p className="text-ink-secondary mb-8">
            A formatted digest of your articles, grouped by topic, for an e-reader or tablet.
          </p>

        {/* Format */}
        <div className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-2 sm:gap-6 py-4 border-t border-edge">
          <label htmlFor="export-format" className="label mb-0 sm:pt-2.5">Format</label>
          <div>
            <select
              id="export-format"
              value={format}
              onChange={(e) => setFormat(e.target.value as ExportFormat)}
              className="field field-mono"
            >
              <option value="pdf">PDF</option>
              <option value="epub">EPUB</option>
            </select>
            <p className="kicker mt-2">
              {format === 'pdf'
                ? 'Fixed layout · tablets and PDF readers'
                : 'Reflowable · most e-readers'}
            </p>
          </div>
        </div>

        {/* Time range */}
        <div className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-2 sm:gap-6 py-4 border-t border-edge">
          <label htmlFor="export-days" className="label mb-0 sm:pt-2.5">Time range</label>
          <div>
            <select
              id="export-days"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="field field-mono"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
            <p className="kicker mt-2">By when you liked or shared, not publish date</p>
          </div>
        </div>

        {/* Sources */}
        <div className="border-t border-edge">
          <div className="label pt-4 mb-0">Sources</div>

          <label className="grid grid-cols-[2rem_1fr] gap-3 py-3 border-b border-edge-subtle cursor-pointer hover:bg-surface-sunken">
            <span className="flex items-start pt-1">
              <input
                type="checkbox"
                checked={includeLiked}
                onChange={(e) => setIncludeLiked(e.target.checked)}
                className="w-4 h-4 accent-ink rounded-none"
              />
            </span>
            <span>
              <span className="block text-[15px] font-semibold text-ink">Liked articles</span>
              <span className="block text-sm text-ink-secondary">Everything you liked in the range.</span>
            </span>
          </label>

          <label className="grid grid-cols-[2rem_1fr] gap-3 py-3 border-b border-edge-subtle cursor-pointer hover:bg-surface-sunken">
            <span className="flex items-start pt-1">
              <input
                type="checkbox"
                checked={includeShared}
                onChange={(e) => setIncludeShared(e.target.checked)}
                className="w-4 h-4 accent-ink rounded-none"
              />
            </span>
            <span>
              <span className="block text-[15px] font-semibold text-ink">Shared articles</span>
              <span className="block text-sm text-ink-secondary">Everything you shared in the range.</span>
            </span>
          </label>

          <label className="grid grid-cols-[2rem_1fr] gap-3 py-3 border-b border-edge cursor-pointer hover:bg-surface-sunken">
            <span className="flex items-start pt-1">
              <input
                type="checkbox"
                checked={includeDigest}
                onChange={(e) => setIncludeDigest(e.target.checked)}
                className="w-4 h-4 accent-ink rounded-none"
              />
            </span>
            <span className="min-w-0">
              <span className="block text-[15px] font-semibold text-ink">Weekly digest</span>
              <span className="block text-sm text-ink-secondary">Articles from one specific digest.</span>
              {includeDigest && (
                <span className="block mt-3 pr-4" onClick={(e) => e.preventDefault()}>
                  {loadingDigests ? (
                    <span className="kicker">Loading digests…</span>
                  ) : digests.length === 0 ? (
                    <span className="kicker">No digests yet</span>
                  ) : (
                    <select
                      value={selectedDigestId}
                      onChange={(e) => setSelectedDigestId(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="field field-mono"
                    >
                      <option value="">Select a digest</option>
                      {digests.map((digest) => (
                        <option key={digest.id} value={digest.id}>
                          {digest.title} — {new Date(digest.createdAt).toLocaleDateString()}
                        </option>
                      ))}
                    </select>
                  )}
                </span>
              )}
            </span>
          </label>
        </div>

        {/* Primary action */}
        <div className="pt-5">
          <button
            onClick={handleExport}
            disabled={loading || (!includeLiked && !includeDigest && !includeShared)}
            className="btn btn-accent w-full"
          >
            {loading ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {progress || `Generating ${formatName}…`}</>
            ) : (
              <><FileDown className="w-3.5 h-3.5" /> Generate {formatName}</>
            )}
          </button>
          {!includeLiked && !includeDigest && !includeShared && (
            <p className="kicker text-signal mt-2">Pick at least one source</p>
          )}
        </div>

        {/* Notes */}
        <div className="mt-8 border-t border-edge pt-4">
          <div className="kicker mb-2">What's in the file</div>
          <ul className="text-sm text-ink-secondary space-y-1 list-none pl-0">
            <li>— Cover page with article count and date</li>
            <li>— Table of contents grouped by topic</li>
            <li>— AI-categorized articles</li>
            <li>— Article content with metadata and source links</li>
          </ul>
          <div className="kicker mt-5 mb-2">About RSS content</div>
          <p className="text-sm text-ink-secondary">
            Some feeds only ship excerpts. The export carries whatever the feed provided; truncated
            articles keep a link to the full version.
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}
